import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Picto } from './pictos.tsx'
import { navFor, type Profile } from './profile.ts'

/** The bottom tabs of the person's profile. The landmark is named "Navigation" and each tab's accessible name is its full label (the end-to-end
 *  tests find a tab that way); the label under the icon is decoration: on a phone the short word ("Jour", "Engins": M23 item 4, where 11 px
 *  "Équipements" was squeezed at 375), from `sm:` the full name, both at 13 px (the audit's floor; `text-xs` is 12.75 at the app's 17-px root). */
export function BottomNav({ profile, width }: { profile: Profile; width: string }) {
  const { t } = useTranslation()
  const items = navFor(profile)
  return (
    <nav className="fixed inset-x-0 bottom-0 z-10 pb-[var(--gk-safe-bottom)] print:hidden border-t border-slate-200 bg-white/97 shadow-[0_-2px_10px_rgb(0_0_0/0.06)] backdrop-blur dark:border-night-line dark:bg-night-card/97" aria-label="Navigation" data-testid="bottom-nav" data-profile={profile}>
      <ul className={`mx-auto flex ${width}`}>
        {items.map((n) => (
          <li key={n.to} className="min-w-0 flex-1">
            <Link to={n.to} activeOptions={{ exact: n.to === '/' }} aria-label={t(n.key)} data-testid={`tab-${n.to === '/' ? 'today' : n.to.slice(1)}`}
              className="group relative flex min-h-16 flex-col items-center justify-center gap-0.5 px-0.5 text-[13px] font-medium leading-4 text-slate-600 dark:text-slate-300 [&.active]:font-bold [&.active]:text-brand dark:[&.active]:text-fairway">
              <span aria-hidden className="absolute inset-x-3 top-0 h-1 rounded-b-full bg-transparent group-[.active]:bg-brand dark:group-[.active]:bg-fairway" />
              <Picto name={n.icon} className="size-6" />
              <span aria-hidden className="whitespace-nowrap tracking-tight sm:hidden" data-testid="tab-short">{t(n.short ?? n.key)}</span>
              <span aria-hidden className="hidden whitespace-nowrap tracking-tight sm:inline" data-testid="tab-full">{t(n.key)}</span>
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  )
}
